import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, Store, ofActionSuccessful } from '@ngxs/store';
import { AuthState, Logout } from '@pharvio/accounts/admin-account/data-access';
import { BranchesState } from '@pharvio/branches/data-access';
import {
  IS_PLATFORM_BROWSER,
  OfflineModeService,
  inputNotNil,
  toFeatureName,
  toQueryParamKeys,
  toRoutePattern
} from '@pharvio/core/shared';
import { TrackService } from '@pharvio/core/ui';
import { PosState } from '@pharvio/pos/data-access';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

/** Features whose first open has already been recorded, so `feature_opened`
 *  can report `firstTime` honestly instead of hardcoding it. */
const SEEN_FEATURES_KEY = 'pharvio.analytics.seenFeatures';

@Injectable({ providedIn: 'root' })
export class AnalyticsBootstrapService {
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly track = inject(TrackService);
  private readonly isBrowser = inject(IS_PLATFORM_BROWSER);
  private readonly destroyRef = inject(DestroyRef);
  private readonly offlineMode = inject(OfflineModeService);

  private identifiedUserId: string | null = null;
  private currentRoute: string | null = null;
  private currentFeature: string | null = null;
  private screenOpenedAt = 0;
  private seenFeatures = new Set<string>();

  readonly offline = this.offlineMode.isOffline;

  init(): void {
    this.seenFeatures = this.readSeenFeatures();
    this.watchPageViews();
    this.watchIdentity();
    this.watchBranch();
    this.watchShift();
    this.watchLogout();
    this.watchPageExit();
  }

  private watchPageViews(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
        debounceTime(10)
      )
      .subscribe((e) => {
        // The screen being left is measured here rather than in each
        // component's ngOnDestroy — that only covers in-app navigation, and
        // misses the tab close and refresh that end most POS sessions.
        this.flushScreenTime();

        const route = toRoutePattern(e.urlAfterRedirects);
        const feature = toFeatureName(e.urlAfterRedirects);
        this.currentRoute = route;
        this.currentFeature = feature;
        this.screenOpenedAt = Date.now();

        if (!this.offline()) {
          this.track.setContext({ route });

          this.track.track({
            name: 'page_viewed',
            props: {
              route,
              title: this.title.getTitle(),
              feature,
              queryParamKeys: toQueryParamKeys(e.urlAfterRedirects)
            }
          });

          this.trackFeatureOpened(feature);
        }
      });
  }

  private trackFeatureOpened(feature: string): void {
    const firstTime = !this.seenFeatures.has(feature);
    if (firstTime) {
      this.seenFeatures.add(feature);
      this.writeSeenFeatures();
    }
    this.track.track({ name: 'feature_opened', props: { feature, firstTime } });
  }

  private flushScreenTime(): void {
    if (!this.currentRoute || !this.screenOpenedAt) return;
    const seconds = Math.round((Date.now() - this.screenOpenedAt) / 1000);
    const route = this.currentRoute;
    const feature = this.currentFeature ?? toFeatureName(route);
    this.screenOpenedAt = 0;
    if (seconds <= 0) return;
    this.track.track({
      name: 'time_on_screen',
      props: { route, feature, seconds }
    });
  }

  private watchPageExit(): void {
    if (!this.isBrowser) return;
    // `pagehide` fires for tab close, refresh and bfcache; `visibilitychange`
    // covers mobile/OS-level backgrounding that never fires `pagehide`.
    window.addEventListener('pagehide', () => this.flushScreenTime());
    this.document?.addEventListener?.('visibilitychange', () => {
      if (this.document?.visibilityState === 'hidden') this.flushScreenTime();
    });
  }

  private get document(): Document | null {
    return this.isBrowser ? window.document : null;
  }

  private watchIdentity(): void {
    this.store
      .select(AuthState.currentUser)
      .pipe(
        filter((u) => inputNotNil(u)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((user) => {
        // Cleared on logout, so signing back in as the same user re-identifies.
        if (this.identifiedUserId === user.id) return;
        this.identifiedUserId = user.id;

        if (!this.offline()) {
          this.track.identifyUser({
            userId: user.id,
            tenantId: user.tenantId ?? '',
            role: user.roles.join(',') ?? '',
            // tenantTier is intentionally omitted — no plan tier is exposed to
            // the ERP client yet, and a hardcoded value poisons segmentation.
            signupMonth: user.createdAt?.slice(0, 7) ?? '',
            branchCount: (user.branchIds && user.branchIds.length) ?? 0,
            // Raw values — TrackService masks them before they leave the browser.
            name: [user.firstName, user.lastName].filter(Boolean).join(' '),
            email: user.email
          });
        }
      });
  }

  /** ERP writes are branch-scoped, and the active branch changes mid-session —
   *  without it there is no answering "which branch actually uses this". */
  private watchBranch(): void {
    this.store
      .select(BranchesState.selectedId)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((branchId) => this.track.setContext({ branchId }));
  }

  private watchShift(): void {
    this.store
      .select(PosState.currentShift)
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((shift) => this.track.setContext({ shiftId: shift?.id }));
  }

  private watchLogout(): void {
    this.actions$
      .pipe(ofActionSuccessful(Logout), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.flushScreenTime();
        this.track.track({ name: 'erp_logout', props: {} });
        this.identifiedUserId = null;
        this.track.resetIdentity();
      });
  }

  private readSeenFeatures(): Set<string> {
    if (!this.isBrowser) return new Set();
    try {
      const raw = window.localStorage.getItem(SEEN_FEATURES_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? (parsed as string[]) : []);
    } catch {
      return new Set();
    }
  }

  private writeSeenFeatures(): void {
    if (!this.isBrowser) return;
    try {
      window.localStorage.setItem(
        SEEN_FEATURES_KEY,
        JSON.stringify([...this.seenFeatures])
      );
    } catch {
      // storage blocked — firstTime degrades to "always true", never throws
    }
  }
}
